import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { IonProgressBar } from "@ionic/react";
import { useHlsPlayer } from "../../hooks/useHlsPlayer";

type Props = {
  src?: string;
  thumbnail?: string;
  active: boolean;
  muted: boolean;
  shouldPlay: boolean;
  liked: boolean;
  shared: boolean;
  onToggleLike(): void;
  onShare(): void;
  onToggleMute(): void;
};

export const VideoSlide: React.FC<Props> = ({
  src,
  thumbnail,
  active,
  muted,
  liked,
  shared,
  shouldPlay,
  onToggleLike,
  onShare,
  onToggleMute,
}) => {
  const { videoRef, hasFrame } = useHlsPlayer({
    src,
    active,
    muted,
    shouldPlay,
  });

  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);

  // === Progress tracking ===
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !active) return;

    let raf: number;
    let hasMeta = false;

    const update = () => {
      if (el && hasMeta && el.duration && isFinite(el.duration) && el.duration > 0) {
        const ratio = Math.min(1, Math.max(0, el.currentTime / el.duration));
        setProgress(ratio);
      }
      raf = requestAnimationFrame(update);
    };

    const onMeta = () => {
      hasMeta = true;
      update();
    };

    el.addEventListener("loadedmetadata", onMeta);
    el.addEventListener("durationchange", onMeta);
    raf = requestAnimationFrame(update);

    return () => {
      el.removeEventListener("loadedmetadata", onMeta);
      el.removeEventListener("durationchange", onMeta);
      cancelAnimationFrame(raf);
    };
  }, [videoRef, active]);

  // === Handle tap/click pause/play ===
  const handleTogglePlayPause = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().catch(() => {});
      setPaused(false);
    } else {
      el.pause();
      setPaused(true);
    }
  };

  return (
    <Slide>
      {thumbnail && (
        <Thumb
          src={thumbnail}
          alt=""
          style={{
            opacity: hasFrame ? 0 : 1,
            transitionDelay: hasFrame ? "0.1s" : "0s",
          }}
        />
      )}

      <VideoEl
        ref={videoRef}
        playsInline
        muted={muted}
        controls={false}
        loop
        style={{
          opacity: hasFrame ? 1 : 0,
          transitionDelay: hasFrame ? "0s" : "0s",
        }}
      />

      <TapCatcher onClick={handleTogglePlayPause} />

      <Actions>
        <ActionBtn onClick={onToggleLike}>{liked ? "💖" : "❤️"}</ActionBtn>
        <ActionBtn onClick={onShare}>{shared ? "✅" : "🔗"}</ActionBtn>
        <ActionBtn onClick={onToggleMute}>{muted ? "🔇" : "🔊"}</ActionBtn>
      </Actions>

      <ProgressWrapper>
        <IonProgressBar
          value={progress}
          style={{
            "--background": "rgba(255,255,255,0.25)",
            "--progress-background": "#2db6b4",
            height: "4px",
          }}
        />
      </ProgressWrapper>
    </Slide>
  );
};

/* ---- styles ---- */
const Slide = styled.div`
  position: relative;
  height: 100vh;
  width: 100vw;
  background: #000;
  overflow: hidden;
`;

const Thumb = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: opacity 0.15s ease;

  @media (max-width: 768px) and (orientation: portrait) {
    object-fit: cover;
  }
`;

const VideoEl = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
  transition: opacity 0.15s ease;

  @media (max-width: 768px) and (orientation: portrait) {
    object-fit: cover;
  }
`;

const TapCatcher = styled.div`
  position: absolute;
  inset: 0;
  cursor: pointer;
  z-index: 3;
`;

const Actions = styled.div`
  position: absolute;
  right: 20px;
  bottom: 150px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  z-index: 4;
`;

const ActionBtn = styled.button`
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  font-size: 20px;
  border-radius: 50%;
  padding: 12px;
  cursor: pointer;
`;

const ProgressWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 5;
`;
